import React, { type ReactElement, useMemo } from "react";
import {
  anniversaryGiftDescription,
  anniversaryGiftHeading,
  footer,
  header,
  headerContainer,
  modalContainer,
  modalContent,
  overlayWrapper,
  terms,
  termsAndConditionsLink
} from "./AnniversaryGiftTeaser.css";
import { DeprecatedOverlayDrawer } from "packages/ui-shared/components/OverlayDrawer";
import type { ApolloClient, InMemoryCache } from "@apollo/client";
import { gql, useQuery } from "@apollo/client";
import { createRequestContext, getClient } from "lib/apolloClient";
import { DeprecatedUISharedButton } from "packages/ui-shared/components/Button/Button";
import { CloseIcon } from "components/SvgIcons";
import { useFeatureFlag, useLocale } from "hooks";
import { space } from "@shared/ui.design-system";
import {
  useTrackElementClicked,
  useTrackElementViewed
} from "packages/membership/tracking";
import type { BaseTrackingValues } from "packages/membership/tracking/types";

import { ExperimentFlags } from "@shared/utils.experimentation";
import { useExperimentViewed } from "tracking/hooks/useExperimentViewed";
import { ANNIVERSARY_GIFT_TEASER } from "@membership/graphql";
import type { Query } from "@shared/graphql.types";
import { AnniversaryGiftProductGrid } from "@membership/ui.components";

type Props = {
  isVisible: boolean;
  setNextVisibility: (visible: boolean, callback?: () => void) => void;
  ctaLabels: Record<string, string>;
  children?: ReactElement | null;
  setActiveCTALabel?: (ctaLabelClicked: string) => void;
};

const baseTrackingValues: BaseTrackingValues = {
  target: "anniversary_gift_teaser",
  location: "anniversary_gift_teaser"
};

const GET_MY_MEMBERSHIP_INFO = gql`
  query getMyMembershipInfo {
    me {
      membership {
        subscriptions {
          active {
            sku
            active
            status
            currentTermEnd
            dateCancelled
          }
        }
      }
    }
  }
`;

const apolloClientOptions = () => ({
  client: getClient() as ApolloClient<InMemoryCache>,
  context: createRequestContext({ sessionStrategy: "START_NEW_ANONYMOUS" })
});

export const AnniversaryGiftTeaser: React.FC<Props> = ({
  isVisible,
  setNextVisibility,
  children = null,
  ctaLabels,
  setActiveCTALabel
}) => {
  const [featureFlagLoading, featureFlagValue] = useFeatureFlag(
    ExperimentFlags.SHOW_ANNIVERSARY_GIFT_TEASER,
    "0"
  );
  const trackExperimentViewed = useExperimentViewed();

  const locale = useLocale();
  const urlPrefix = locale === "en-US" ? "/us" : "";
  const { data: myMembership } = useQuery(
    GET_MY_MEMBERSHIP_INFO,
    apolloClientOptions()
  );

  const { data: anniversaryGiftsData } = useQuery<Query>(
    ANNIVERSARY_GIFT_TEASER,
    apolloClientOptions()
  );

  const { ref } = useTrackElementViewed({
    ...baseTrackingValues
  });

  const { elementClicked } = useTrackElementClicked({
    ...baseTrackingValues
  });
  const { gifts, modalHeader, modalDescription, termsAndConditions } =
    anniversaryGiftsData?.getAnniversaryGiftModal || {};

  const { currentTermEnd: membershipEnd } =
    myMembership?.me?.membership?.subscriptions?.active?.[0] || {};

  const daysUntilMembershipEnd: number | undefined = useMemo(() => {
    if (membershipEnd) {
      const membershipEndDate = new Date(membershipEnd);
      const today = new Date();
      const diffInMs = membershipEndDate.getTime() - today.getTime();
      const num24hourPeriods = Math.ceil(diffInMs / (1000 * 60 * 60 * 24)) - 1;
      if (num24hourPeriods === 0) {
        if (membershipEndDate.getDate() !== today.getDate()) {
          return 1;
        }
      }
      return num24hourPeriods;
    }
  }, [membershipEnd]);

  const isValidUserContext = useMemo(() => {
    if (myMembership && daysUntilMembershipEnd) {
      const { active, status, sku } =
        myMembership.me?.membership?.subscriptions?.active?.[0] || {};
      if (sku?.startsWith("BP_PLUS") && active && status === "active") {
        return daysUntilMembershipEnd <= 31;
      }
      return false;
    }
  }, [myMembership, daysUntilMembershipEnd]);

  if (
    !featureFlagLoading &&
    featureFlagValue?.length &&
    isValidUserContext &&
    gifts?.length
  ) {
    if (featureFlagValue === "1") {
      // User satisfied all conditions AND is in the do-show bucket
      trackExperimentViewed(ExperimentFlags.SHOW_ANNIVERSARY_GIFT_TEASER, 1);
      const daysAsText = daysUntilMembershipEnd === 1 ? "day" : "days";

      return (
        <DeprecatedOverlayDrawer isOpen={isVisible} className={overlayWrapper}>
          <div className={modalContainer} ref={ref}>
            <div className={modalContent}>
              <div className={header}>
                <div className={headerContainer}>
                  <h2
                    className={anniversaryGiftHeading}
                  >{`${modalHeader} ${daysUntilMembershipEnd} ${daysAsText}...`}</h2>
                  <CloseIcon
                    cursor="pointer"
                    boxSize={space[24]}
                    aria-label="Close"
                    onClick={() => {
                      setNextVisibility(false);
                      elementClicked({
                        details: "close_icon"
                      });
                    }}
                  />
                </div>

                <p className={anniversaryGiftDescription}>{modalDescription}</p>
              </div>
              <AnniversaryGiftProductGrid
                gifts={gifts}
                setNextVisibility={setNextVisibility}
                elementClicked={elementClicked}
              />
            </div>
            <div className={footer}>
              <p className={terms}>
                {termsAndConditions}{" "}
                <a
                  href={`${urlPrefix}/legal/terms-and-conditions`}
                  className={termsAndConditionsLink}
                  onClick={() => {
                    setNextVisibility(false);
                    elementClicked({
                      details: "terms_and_conditions_link",
                      contentUrl: `${window.location.origin}${urlPrefix}/legal/terms-and-conditions`
                    });
                  }}
                >
                  T&C's apply
                </a>
              </p>
              <DeprecatedUISharedButton
                variant="primary"
                handleClick={() => {
                  setActiveCTALabel?.(ctaLabels.EMAIL_ME_WHEN_ITS_HERE);
                  setNextVisibility(false);
                  elementClicked({
                    details: "email_me_when_its_here"
                  });
                }}
              >
                {ctaLabels.EMAIL_ME_WHEN_ITS_HERE}
              </DeprecatedUISharedButton>
              <DeprecatedUISharedButton
                variant="tertiary"
                handleClick={() => {
                  setNextVisibility(false);
                  elementClicked({
                    details: "close"
                  });
                }}
              >
                {ctaLabels.CLOSE}
              </DeprecatedUISharedButton>
            </div>
          </div>
        </DeprecatedOverlayDrawer>
      );
    }
    // User satisfied all conditions BUT is in the no-show bucket
    trackExperimentViewed(ExperimentFlags.SHOW_ANNIVERSARY_GIFT_TEASER, 0);
    setNextVisibility(false); // Mark modal as seen to conclude the experiment, even though it wasn't displayed to the user
    return null;
  }

  if (isValidUserContext === false) {
    return children;
  }

  return null;
};
